import { useEffect, useState } from 'react';

import { Save, Settings2 } from 'lucide-react';

import {
  AVISO_DE_ACAO_INDISPONIVEL,
  mostrarAviso,
} from '@/modulos/operacao/comum/cadastroIndisponivel';
import { ErrorState } from '@/ui/ErrorState';
import { PageHeader } from '@/ui/PageHeader';
import { Spinner } from '@/ui/Spinner';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/ui/base/card';
import { Checkbox } from '@/ui/base/checkbox';

import {
  ACOES_EM_LOTE,
  CHAVES,
  ETAPAS_DE_FECHAMENTO,
  useConfiguracao,
  type AcaoEmLote,
  type EtapaDeFechamento,
  type MatrizDeAcoes,
} from './dados';

const MATRIZ_VAZIA: MatrizDeAcoes = {
  notificar_sme: [],
  exportar_sof: [],
  escalonar_dre: [],
};

/**
 * `/admin/configuracoes/acoes-em-lote` — em quais etapas do
 * fechamento mensal cada ação em lote fica disponível.
 *
 * Uma marcação diz "nesta etapa, esta ação aparece no cockpit". Desmarcar não
 * desfaz o que já foi feito; só tira a ação do menu daquela etapa.
 *
 * "SALVAR MATRIZ" NÃO GRAVA. `/configuracoes` não existe na API e a tabela
 * `configuracoes` não tem política de escrita. O botão mostra o aviso honesto de
 * indisponibilidade em vez de fingir sucesso
 * (`modulos/operacao/comum/cadastroIndisponivel.ts`). As marcações continuam
 * como o usuário deixou, porque são estado local da tela — e a tela diz isso.
 */
export function MatrizAcoesLotePage() {
  const consulta = useConfiguracao<MatrizDeAcoes>(CHAVES.matrizAcoesLote);

  const [matriz, setMatriz] = useState<MatrizDeAcoes>(MATRIZ_VAZIA);

  useEffect(() => {
    if (consulta.data) {
      setMatriz(consulta.data);
    }
  }, [consulta.data]);

  function alternar(acao: AcaoEmLote, etapa: EtapaDeFechamento) {
    setMatriz((atual) => {
      const etapasDaAcao = atual[acao];
      // A asserção é por causa da chave computada de tipo união: o TypeScript
      // alarga `{ ...atual, [acao]: ... }` e perde a forma exata da matriz.
      return {
        ...atual,
        [acao]: etapasDaAcao.includes(etapa)
          ? etapasDaAcao.filter((item) => item !== etapa)
          : [...etapasDaAcao, etapa],
      } as MatrizDeAcoes;
    });
  }

  function salvar() {
    // A tabela `configuracoes` não aceita escrita hoje. O aviso é honesto: nada
    // foi gravado, e as marcações abaixo seguem sendo só estado desta tela.
    mostrarAviso(AVISO_DE_ACAO_INDISPONIVEL);
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Matriz de ações em lote"
        description="Define em quais etapas do fechamento mensal cada ação em lote fica disponível no cockpit financeiro."
      />

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Settings2 className="h-4 w-4 text-primary" aria-hidden="true" />
            Etapas elegíveis por ação
          </CardTitle>
          <CardDescription>
            Desmarcar uma etapa apenas retira a ação do menu daquela fase; nada do que já foi
            executado é desfeito.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {consulta.isError ? (
            <ErrorState
              variant="load-failed"
              description="Não foi possível carregar a matriz de ações agora. Verifique sua conexão e tente novamente."
              primaryAction={{ label: 'Tentar de novo', onClick: () => void consulta.refetch() }}
            />
          ) : consulta.isLoading ? (
            <Spinner />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <caption className="sr-only">
                    Etapas do fechamento em que cada ação em lote fica disponível
                  </caption>
                  <thead>
                    <tr className="border-b text-left">
                      <th scope="col" className="py-2 pr-3 font-medium text-muted-foreground">
                        Ação
                      </th>
                      {ETAPAS_DE_FECHAMENTO.map((etapa) => (
                        <th
                          key={etapa.id}
                          scope="col"
                          className="px-2 py-2 text-center font-medium text-muted-foreground"
                        >
                          {etapa.rotulo}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ACOES_EM_LOTE.map((acao) => (
                      <tr key={acao.id} className="border-b last:border-0">
                        <th scope="row" className="py-3 pr-3 text-left align-top font-normal">
                          <span className="block font-medium">{acao.rotulo}</span>
                          <span className="block text-xs text-muted-foreground">
                            {acao.descricao}
                          </span>
                        </th>
                        {ETAPAS_DE_FECHAMENTO.map((etapa) => (
                          <td key={etapa.id} className="px-2 py-3 text-center">
                            <Checkbox
                              checked={matriz[acao.id].includes(etapa.id)}
                              onCheckedChange={() => alternar(acao.id, etapa.id)}
                              aria-label={`${acao.rotulo} na etapa ${etapa.rotulo}`}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex justify-end">
                <Button onClick={salvar}>
                  <Save className="mr-1.5 h-4 w-4" aria-hidden="true" />
                  Salvar matriz
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default MatrizAcoesLotePage;
